/**
 * Search feature
 * SCR-003: Global Search Modal
 * SCR-106: Search Interface
 *
 * Provides:
 * - Global search modal with keyboard shortcuts
 * - Saved searches (API-backed)
 * - Search history (API-backed)
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { GlobalSearchModal } from './GlobalSearchModal';

interface SearchContextValue {
  query: string;
  setQuery: (query: string) => void;
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Global keyboard shortcut: Cmd/Ctrl + K to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      // Also support '/' to open search when not in an input
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <SearchContext.Provider
      value={{
        query,
        setQuery,
        isSearchOpen,
        openSearch: () => setIsSearchOpen(true),
        closeSearch: () => setIsSearchOpen(false),
      }}
    >
      {children}
      <GlobalSearchModal />
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}

// Alias for backwards compatibility with Header component
export function useSearchModal() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchModal must be used within a SearchProvider');
  }
  return {
    isOpen: context.isSearchOpen,
    openSearch: context.openSearch,
    closeSearch: context.closeSearch,
    query: context.query,
    setQuery: context.setQuery,
  };
}

// Export GlobalSearchModal component
export { GlobalSearchModal } from './GlobalSearchModal';

// Export saved searches and history components (SCR-106)
export { SavedSearchesPanel } from './components/SavedSearchesPanel';
export { SearchHistoryPanel } from './components/SearchHistoryPanel';

// Export hooks
export {
  useSavedSearches,
  useCreateSavedSearch,
  useDeleteSavedSearch,
  useSearchHistory,
  useClearSearchHistory,
  searchKeys,
  type SavedSearch,
  type SavedSearchCreate,
  type SavedSearchesResponse,
  type SearchHistoryEntry,
  type SearchHistoryResponse,
} from './hooks/useSearchPersistence';
